import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { t, pick } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users, HeartHandshake, Briefcase, MessageCircle, GraduationCap, Send, ArrowLeft } from "lucide-react";
import PaywallDialog from "@/components/PaywallDialog";
import UsageMeter from "@/components/UsageMeter";
import { useUsage } from "@/context/UsageContext";

const ICONS = {
  users: Users,
  "heart-handshake": HeartHandshake,
  briefcase: Briefcase,
  "message-circle": MessageCircle,
  "graduation-cap": GraduationCap,
};

export default function Practice() {
  const { lang } = useLang();
  const { refresh: refreshUsage } = useUsage();
  const [contexts, setContexts] = useState([]);
  const [active, setActive] = useState(null);
  const [scenario, setScenario] = useState("");
  const [msg, setMsg] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [paywall, setPaywall] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/content/contexts").then((r) => setContexts(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" });
  }, [history, busy]);

  const startChat = (ctx, s = "") => {
    setActive(ctx);
    setScenario(s);
    const opening = {
      es: `Estoy aquí para acompañarte a practicar Comunicación No Violenta en el contexto de ${pick(ctx.name, "es")}. Cuéntame qué situación quieres explorar.`,
      en: `I'm here to help you practice Nonviolent Communication in the ${pick(ctx.name, "en")} context. Tell me what situation you'd like to explore.`,
      it: `Sono qui per aiutarti a praticare la Comunicazione Nonviolenta nel contesto di ${pick(ctx.name, "it")}. Raccontami quale situazione vuoi esplorare.`,
    }[lang];
    setHistory([{ id: `m-${Date.now()}-open`, role: "assistant", content: opening }]);
  };

  const send = async () => {
    if (!msg.trim() || busy) return;
    const userMsg = msg.trim();
    const newHist = [...history, { id: `m-${Date.now()}-u`, role: "user", content: userMsg }];
    setHistory(newHist);
    setMsg("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/coach", {
        context_id: active.id,
        scenario,
        history: newHist.slice(0, -1),
        message: userMsg,
        language: lang,
      });
      setHistory([...newHist, { id: `m-${Date.now()}-a`, role: "assistant", content: data.reply }]);
      refreshUsage();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402 && detail?.upgrade) {
        setPaywall(true);
      } else {
        toast.error(typeof detail === "string" ? detail : t(lang, "error"));
      }
      setHistory(newHist);
    } finally {
      setBusy(false);
    }
  };

  if (!active) {
    return (
      <div className="space-y-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-accent font-medium">{t(lang, "practice")}</div>
            <h1 className="font-heading text-4xl font-light mt-2">{t(lang, "choose_context")}</h1>
            <p className="text-muted-foreground mt-3 max-w-2xl">{t(lang, "practice_intro")}</p>
          </div>
          <UsageMeter />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {contexts.map((c) => {
            const Icon = ICONS[c.icon] || MessageCircle;
            return (
              <div key={c.id} className="p-6 rounded-xl border border-border bg-card card-lift" data-testid={`context-card-${c.id}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-secondary text-primary flex items-center justify-center">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-xl font-medium">{pick(c.name, lang)}</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">{pick(c.description, lang)}</p>
                <div className="space-y-2">
                  {c.scenarios.map((s, i) => {
                    const scenarioText = pick(s, lang);
                    return (
                      <button
                        key={`${c.id}-scen-${i}-${scenarioText.slice(0, 20)}`}
                        onClick={() => startChat(c, scenarioText)}
                        className="w-full text-left text-sm p-3 rounded-lg border border-border hover:bg-secondary/60 transition-colors"
                        data-testid={`scenario-${c.id}-${i}`}
                      >
                        {scenarioText}
                      </button>
                    );
                  })}
                  <Button variant="ghost" className="w-full mt-2 rounded-full" onClick={() => startChat(c, "")} data-testid={`context-free-${c.id}`}>
                    {t(lang, "start_practice")}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        <PaywallDialog open={paywall} onOpenChange={setPaywall} limitKey="ai_coach" page="practice" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" onClick={() => setActive(null)} data-testid="practice-back">
          <ArrowLeft className="w-4 h-4" /> {t(lang, "choose_context")}
        </button>
        <UsageMeter />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-accent font-medium">{t(lang, "context")}</div>
        <h1 className="font-heading text-3xl font-medium mt-1">{pick(active.name, lang)}</h1>
        {scenario && <p className="text-muted-foreground mt-2">{scenario}</p>}
      </div>

      <div ref={scrollRef} className="h-[520px] overflow-y-auto p-6 rounded-2xl border border-border bg-card space-y-4" data-testid="chat-window">
        {history.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] p-4 rounded-2xl leading-relaxed ${
              m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary/70"
            }`}>
              {m.content}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="max-w-[85%] p-4 rounded-2xl bg-secondary/70 text-muted-foreground italic">
              {t(lang, "coach_thinking")}
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-3">
        <Input
          data-testid="chat-input"
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={t(lang, "type_message")}
          className="p-4"
        />
        <Button data-testid="chat-send" onClick={send} disabled={busy || !msg.trim()} className="rounded-full gap-2">
          <Send className="w-4 h-4" /> {t(lang, "send")}
        </Button>
      </div>
      <PaywallDialog open={paywall} onOpenChange={setPaywall} limitKey="ai_coach" page="practice" />
    </div>
  );
}

